'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { BackButton } from './BackButton';

interface ProjectSubpageLayoutProps {
  projectId: string;
  backLabel: string;
  children: ReactNode;
  onBackClick?: (e: React.MouseEvent) => void;
  /** Page title shown in the sticky header */
  title?: string;
  /** Action buttons rendered on the right side of the header */
  headerActions?: ReactNode;
}

export function ProjectSubpageLayout({
  projectId,
  backLabel,
  children,
  onBackClick,
  title,
  headerActions,
}: ProjectSubpageLayoutProps) {
  return (
    <>
      {/* Sticky header bar — iOS-style inline navigation bar */}
      {title && (
        <div className="sticky top-0 z-30 backdrop-blur-xl">
          <div className="flex items-center h-16 px-3 lg:px-4 max-w-[1200px] mx-auto relative">
            <BackButton
              href={`/project/${projectId}`}
              onClick={onBackClick}
              label={backLabel}
            />

            {/* Centered title — larger */}
            <span className="absolute left-1/2 -translate-x-1/2 text-page-title truncate max-w-[55%] pointer-events-none">
              {title}
            </span>

            {/* Trailing actions */}
            {headerActions && (
              <div className="ms-auto flex items-center gap-1 shrink-0">
                {headerActions}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto py-4 px-4 sm:px-6 lg:px-8">
        {/* Fallback desktop back link — only when title is not provided (backward compat) */}
        {!title && (
          <div className="mb-6">
            <Link
              href={`/project/${projectId}`}
              onClick={onBackClick}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            >
              <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
              <span>{backLabel}</span>
            </Link>
          </div>
        )}
        {children}
      </div>
    </>
  );
}
